import React, { useCallback, useContext, useEffect } from 'react';
import { formatUnits, parseUnits } from 'ethers/lib/utils';
import { t } from 'i18next';
import { Trans } from 'react-i18next';
import { AmountInput, Button, ButtonStyle, ErrorBadge, ErrorLevel, ErrorList, Icon, IconNames, Input, InputSize, Paragraph, } from '@sovryn/ui';
import { RSK_CHAIN_ID } from '../../../../../../config/chains';
import { AmountRenderer } from '../../../../../2_molecules/AmountRenderer/AmountRenderer';
import { MaxButton } from '../../../../../2_molecules/MaxButton/MaxButton';
import { USD } from '../../../../../../constants/currencies';
import { useAccount } from '../../../../../../hooks';
import { useDollarValue } from '../../../../../../hooks/useDollarValue';
import { useTokenDetailsByAsset } from '../../../../../../hooks/useTokenDetailsByAsset';
import { translations } from '../../../../../../locales/i18n';
import { toWei } from '../../../../../../utils/math';
import { SendFlowContext, SendFlowStep } from '../../../contexts/sendflow';
import { useBridgeService } from '../../../hooks/useBridgeService';
import { useBridgeValidation } from '../../../hooks/useBridgeValidation';
import { useERC20BridgeLocked } from '../../../hooks/useERC20BridgeLocked';
import { useTokenBalance } from '../../../hooks/useTokenBalance';
export const AmountScreen = () => {
    var _a, _b, _c;
    const { account } = useAccount();
    const { set, token, chainId, amount, receiver } = useContext(SendFlowContext);
    const bridgeService = useBridgeService();
    const assetDetails = useTokenDetailsByAsset(token, RSK_CHAIN_ID);
    const { data: tokenBalance } = useTokenBalance(token, RSK_CHAIN_ID);
    const isBridgeLocked = useERC20BridgeLocked();
    const balance = formatUnits(tokenBalance || '0', assetDetails === null || assetDetails === void 0 ? void 0 : assetDetails.decimals);
    const onContinueClick = useCallback(() => set(prevState => (Object.assign(Object.assign({}, prevState), { step: SendFlowStep.REVIEW }))), [set]);
    const setAmount = useCallback((value) => set(prevState => (Object.assign(Object.assign({}, prevState), { amount: value }))), [set]);
    const setReceiver = useCallback((value) => set(prevState => (Object.assign(Object.assign({}, prevState), { receiver: value }))), [set]);
    const onMaximumAmountClick = useCallback(() => setAmount((balance || '0').toString()), [balance, setAmount]);
    useEffect(() => {
        if (!receiver && account) {
            setReceiver(account);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const { isValid, messages } = useBridgeValidation({
        sourceChain: RSK_CHAIN_ID,
        targetChain: chainId,
        asset: token,
        amount: parseUnits(amount || '0', assetDetails === null || assetDetails === void 0 ? void 0 : assetDetails.decimals).toString(),
        receiver,
    });
    const { usdValue } = useDollarValue(token, amount !== '' ? toWei(amount).toString() : '0', RSK_CHAIN_ID);
    return (React.createElement("div", null,
        chainId && (React.createElement(Paragraph, { className: "flex text-base font-medium items-center mb-6 gap-2" },
            React.createElement(React.Fragment, null,
                t(translations.erc20Bridge.mainScreen.sending),
                ' ', (_a = bridgeService.getNetworkConfig(RSK_CHAIN_ID)) === null || _a === void 0 ? void 0 :
                _a.name,
                React.createElement(Icon, { icon: IconNames.ARROW_RIGHT, size: 12 }), (_b = bridgeService.getNetworkConfig(chainId)) === null || _b === void 0 ? void 0 :
                _b.name))),
        React.createElement("div", { className: "w-full flex flex-row justify-between items-center mb-1" },
            React.createElement(Paragraph, { className: "text-sm font-medium" }, t(translations.erc20Bridge.mainScreen.tabs.sendLabel)),
            token && balance && (React.createElement(MaxButton, { onClick: onMaximumAmountClick, value: balance || '0', token: token, dataAttribute: "bridge-from-max", chainId: RSK_CHAIN_ID }))),
        React.createElement("div", { className: "mb-6" },
            React.createElement(AmountInput, { value: amount, onChangeText: setAmount, label: t(translations.common.amount), unit: token, min: 0, className: "w-full mb-0 max-w-full", placeholder: "0" }),
            React.createElement("div", { className: "flex justify-end text-tiny text-gray-30 mt-1" },
                React.createElement(AmountRenderer, { value: usdValue, suffix: USD }))),
        React.createElement(Paragraph, { className: "mb-1 text-sm font-medium" },
            React.createElement(Trans, { i18nKey: t(translations.erc20Bridge.send.addressLabel, {
                    network: (_c = bridgeService.getNetworkConfig(chainId)) === null || _c === void 0 ? void 0 : _c.name,
                }) })),
        React.createElement(Input, { value: receiver, onChangeText: setReceiver, className: "w-full mb-6 max-w-full", placeholder: "0x", size: InputSize.large }),
        React.createElement(ErrorBadge, { level: ErrorLevel.Warning, message: React.createElement(Trans, { i18nKey: t(translations.erc20Bridge.send.warning) }) }),
        React.createElement(ErrorList, { errors: messages.map(message => ({
                level: ErrorLevel.Critical,
                message,
            })) }),
        isBridgeLocked ? (React.createElement(ErrorBadge, { level: ErrorLevel.Warning, message: t(translations.maintenanceMode.erc20Bridge) })) : (React.createElement(Button, { onClick: onContinueClick, text: t(translations.common.buttons.continue), className: "w-full mt-6", style: ButtonStyle.secondary, dataAttribute: "funding-send-instructions-confirm", disabled: !isValid || isBridgeLocked }))));
};
